import { contrastRatio, deriveTokens, type ThemeSeed } from './deriveTokens';
import { SEEDS, type ThemeSeedEntry } from './presets';
import { DEFAULT_PRESET_ID, type ThemeCategory, type ThemePreset } from './tokens';

/**
 * The resolved preset registry: seeds in, complete presets out. Everything is
 * derived once at module load, so lookups are plain map reads.
 */

const build = (entry: ThemeSeedEntry): ThemePreset => ({
  meta: {
    id: entry.id,
    name: entry.name,
    category: entry.category,
    description: entry.description,
    designedFor: entry.designedFor,
    sortOrder: entry.sortOrder,
  },
  light: deriveTokens(entry.seed, 'light'),
  dark: deriveTokens(entry.seed, 'dark'),
});

const PRESETS: ThemePreset[] = SEEDS.map(build);
const BY_ID = new Map(PRESETS.map((p) => [p.meta.id, p]));
const SEED_BY_ID = new Map(SEEDS.map((s) => [s.id, s.seed]));

/** Resolved once so a bad DEFAULT_PRESET_ID fails loudly at load. */
export const defaultPreset: ThemePreset = (() => {
  const found = BY_ID.get(DEFAULT_PRESET_ID);
  if (!found) {
    throw new Error(`DEFAULT_PRESET_ID "${DEFAULT_PRESET_ID}" is not in the preset catalogue.`);
  }
  return found;
})();

const defaultSeed = SEED_BY_ID.get(DEFAULT_PRESET_ID) as ThemeSeed;

export const allPresets = (): ThemePreset[] => PRESETS;

/** Looks a preset up, falling back to the default. Never throws — a stored id can outlive its preset. */
export const getPreset = (id: string | null | undefined): ThemePreset =>
  (id ? BY_ID.get(id) : undefined) ?? defaultPreset;

/** The seed a preset was built from, falling back to the default's. */
export const getPresetSeed = (id: string | null | undefined): ThemeSeed =>
  (id ? SEED_BY_ID.get(id) : undefined) ?? defaultSeed;

/** True when the id names a preset that exists. */
export const isKnownPreset = (id: string | null | undefined): id is string =>
  Boolean(id && BY_ID.has(id));

/** Declaration order, so picker sections are stable rather than alphabetical. */
export const CATEGORY_ORDER: ThemeCategory[] = [
  'Camptain',
  'Enterprise',
  'Premium Dark',
  'High-Contrast',
];

export interface PresetGroup {
  category: ThemeCategory;
  presets: ThemePreset[];
}

/** Presets grouped by category. Empty categories are dropped. */
export const presetsByCategory = (): PresetGroup[] =>
  CATEGORY_ORDER.map((category) => ({
    category,
    presets: PRESETS.filter((p) => p.meta.category === category).sort(
      (a, b) => a.meta.sortOrder - b.meta.sortOrder,
    ),
  })).filter((g) => g.presets.length > 0);

export interface ContrastReport {
  /** Body text on the default content surface. WCAG AA wants 4.5. */
  bodyOnPaper: number;
  /** Secondary text on the same surface. */
  secondaryOnPaper: number;
  /** Label on a filled primary button. */
  onPrimary: number;
  /** Sidebar text on sidebar chrome. */
  sidebarText: number;
  /** True when every measured pair clears AA. */
  passesAA: boolean;
}

/** Measures a preset's key text/background pairs. Reported, not enforced. */
export const contrastReport = (preset: ThemePreset, mode: 'light' | 'dark'): ContrastReport => {
  const t = preset[mode];
  const bodyOnPaper = contrastRatio(t.text.primary, t.surface.paper);
  const secondaryOnPaper = contrastRatio(t.text.secondary, t.surface.paper);
  const onPrimary = contrastRatio(
    t.component.button.primaryText,
    t.component.button.primaryBackground,
  );
  const sidebarText = contrastRatio(t.component.sidebar.text, t.component.sidebar.background);

  return {
    bodyOnPaper,
    secondaryOnPaper,
    onPrimary,
    sidebarText,
    passesAA:
      bodyOnPaper >= 4.5 && secondaryOnPaper >= 4.5 && onPrimary >= 4.5 && sidebarText >= 4.5,
  };
};
